import {
	getGetAccountPaymentMethodsQueryKey,
	useDeleteAccountPaymentMethod,
	useGetAccountPaymentMethods,
	useSetDefaultAccountPaymentMethod,
} from '@cocoonspace/sdk-js/domains/account/account'
import type { AccountPaymentMethod } from '@cocoonspace/sdk-js/types/accountPaymentMethod'
import { useQueryClient } from '@tanstack/react-query'
import { Landmark, Trash2 } from 'lucide-react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { toast } from 'sonner'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '~/components/ui/alert-dialog'
import { Button } from '~/components/ui/button'
import { Card } from '~/components/ui/card'
import { PaymentMethodCardIcon } from '~/domains/payments/components/payment-method-card-icon'
import { useTracking } from '~/providers/tracking-provider'

export const PaymentMethodsList = ({
	onSetDefault,
}: {
	onSetDefault?: VoidFunction
}) => {
	const router = useRouter()
	const queryClient = useQueryClient()
	const { t } = useTranslation()
	const { track } = useTracking()

	const { data: paymentMethods } = useGetAccountPaymentMethods({
		query: {
			select: (res) => res.data.filter((pm) => pm.type === 'card') || [],
		},
	})

	const pmDefaultMutation = useSetDefaultAccountPaymentMethod()
	const deletePmMutation = useDeleteAccountPaymentMethod()

	const setDefault = (pm: AccountPaymentMethod) =>
		pmDefaultMutation.mutateAsync({ methodId: pm.id! }).then(() => {
			onSetDefault?.()
			toast.success(t('myAccountPaymentMethods:notifications.cardSetDefault'))
			queryClient.invalidateQueries({
				queryKey: getGetAccountPaymentMethodsQueryKey(),
			})
			track.paymentMethodUpdate({ action: 'set-default', route: router.route })
		})

	const onDelete = (pm: AccountPaymentMethod) =>
		deletePmMutation.mutateAsync({ methodId: pm.id! }).then(() => {
			toast.success(t('myAccountPaymentMethods:notifications.cardDeleted'))
			queryClient.invalidateQueries({
				queryKey: getGetAccountPaymentMethodsQueryKey(),
			})
			track.paymentMethodUpdate({ action: 'delete', route: router.route })
		})

	if (!paymentMethods?.length) return null

	return (
		<div className='flex flex-1 flex-col gap-4'>
			{paymentMethods?.map((paymentMethod) => (
				<Card
					key={paymentMethod.id}
					className='flex flex-col divide-y'
				>
					<div className='flex w-full flex-1 flex-row items-center gap-4 p-4'>
						<div className='px-2 text-[40px]'>
							{paymentMethod.type === 'sepa_credit' && (
								<Landmark
									size={30}
									strokeWidth={1}
								/>
							)}

							{paymentMethod.type === 'card' && (
								<PaymentMethodCardIcon brand={paymentMethod.card?.brand} />
							)}
						</div>

						<div className='relative flex flex-1 flex-col'>
							{paymentMethod.default && (
								<div className='-right-4 -top-4 absolute rounded-tr rounded-bl bg-slate-800 px-6 py-1 text-sm text-white'>
									{t('myAccountPaymentMethods:defaultCard')}
								</div>
							)}

							<div>{paymentMethod.title}</div>

							<strong className='text-lg'>{paymentMethod.name}</strong>

							<div className='flex w-full flex-col md:flex-row md:gap-2'>
								<div>{`**** **** **** ${paymentMethod.card?.last_4}`}</div>

								<div className='md:ml-auto'>
									{t('myAccountPaymentMethods:expires_on', {
										month: paymentMethod.card?.exp_month
											?.toString()
											.padStart(2, '0'),
										year: paymentMethod.card?.exp_year,
									})}
								</div>
							</div>
						</div>
					</div>

					{paymentMethods?.length > 1 && (
						<div className='flex flex-row items-center justify-center gap-4 p-4 md:justify-end'>
							{!paymentMethod.default && (
								<Button
									size='sm'
									variant='outline'
									onClick={() => setDefault(paymentMethod)}
								>
									{t('myAccountPaymentMethods:choose_default')}
								</Button>
							)}

							<AlertDialog>
								<AlertDialogTrigger asChild>
									<Button
										size='sm'
										variant='outline'
										className='gap-1'
									>
										<Trash2 size={16} />

										{t('myAccountPaymentMethods:delete')}
									</Button>
								</AlertDialogTrigger>

								<AlertDialogContent>
									<AlertDialogHeader>
										<AlertDialogTitle>
											{t('myAccountPaymentMethods:confirm_delete')}
										</AlertDialogTitle>
									</AlertDialogHeader>

									<AlertDialogFooter>
										<AlertDialogCancel>
											{t('myAccountPaymentMethods:cancel')}
										</AlertDialogCancel>

										<AlertDialogAction onClick={() => onDelete(paymentMethod)}>
											{t('myAccountPaymentMethods:delete')}
										</AlertDialogAction>
									</AlertDialogFooter>
								</AlertDialogContent>
							</AlertDialog>
						</div>
					)}
				</Card>
			))}
		</div>
	)
}
